import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../api';
import toast from 'react-hot-toast';
import { Loader2, Users, Activity, Trash2, ShieldAlert, CreditCard, Calendar } from 'lucide-react';

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        api.get('/admin/dashboard'),
        api.get('/admin/users')
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load admin data. Are you an admin?');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (id, email) => {
    if (!window.confirm(`CRITICAL WARNING: Are you sure you want to permanently delete user ${email}? This will delete all their data and cancel their subscriptions.`)) {
      return;
    }
    setDeletingId(id);
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success('User deleted successfully');
      setUsers(users.filter(u => u.id !== id));
      setStats(prev => ({ ...prev, total_users: prev.total_users - 1 }));
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const sortedUsers = [...users].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (aVal === null) aVal = '';
    if (bVal === null) bVal = '';

    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  }).filter(u => u.email.toLowerCase().includes(search.toLowerCase()) || (u.display_name && u.display_name.toLowerCase().includes(search.toLowerCase())));

  if (loading) {
    return <div style={{ display: 'flex', height: '100vh', justifyContent: 'center', alignItems: 'center' }}><Loader2 className="text-primary" size={48} style={{ animation: 'spin 2s linear infinite' }} /></div>;
  }

  // Cost estimates
  const plaidCostPerSync = 0.30; // rough estimate
  const estimatedTotalCost = ((stats?.total_api_calls || 0) * plaidCostPerSync).toLocaleString(undefined, {style: 'currency', currency: 'USD'});

  return (
    <>
      <div className="container-main" style={{ animation: 'fade-in 0.4s ease-out' }}>
        <header style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <ShieldAlert size={36} color="var(--danger)" />
          <div>
            <h1 style={{ margin: '0 0 0.25rem 0', fontSize: '2.5rem', color: 'var(--danger)' }}>Admin Command Center</h1>
            <p className="text-muted" style={{ margin: 0 }}>Manage users, view platform metrics, and oversee revenue.</p>
          </div>
        </header>

        {/* KPI Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
          <div className="glass" style={{ padding: '1.5rem', borderRadius: 'var(--radius-md)', borderLeft: '4px solid var(--primary)' }}>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Users size={16} /> Total Users</div>
            <h2 style={{ margin: 0, fontSize: '2rem' }}>{stats?.total_users || 0}</h2>
          </div>
          <div className="glass" style={{ padding: '1.5rem', borderRadius: 'var(--radius-md)', borderLeft: '4px solid var(--success)' }}>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><CreditCard size={16} /> Active Subscribers</div>
            <h2 style={{ margin: 0, fontSize: '2rem', color: 'var(--success)' }}>{stats?.active_subscribers || 0}</h2>
          </div>
          <div className="glass" style={{ padding: '1.5rem', borderRadius: 'var(--radius-md)', borderLeft: '4px solid var(--warning)' }}>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Activity size={16} /> Plaid Linked</div>
            <h2 style={{ margin: 0, fontSize: '2rem' }}>{stats?.connected_plaid || 0}</h2>
          </div>
          <div className="glass" style={{ padding: '1.5rem', borderRadius: 'var(--radius-md)', borderLeft: '4px solid var(--danger)' }}>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Activity size={16} /> Est. API Cost</div>
            <h2 style={{ margin: 0, fontSize: '2rem', color: 'var(--danger)' }}>{estimatedTotalCost}</h2>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>{stats?.total_api_calls || 0} calls @ ~$0.30/ea</div>
          </div>
        </div>

        {/* User Data Grid */}
        <div className="glass" style={{ borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
          <div style={{ padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--surface-border)' }}>
            <h3 style={{ margin: 0 }}>Registered Users</h3>
            <input 
              type="text" 
              className="input-base" 
              placeholder="Search by email..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '300px' }}
            />
          </div>
          
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.02)' }}>
                  <th style={{ padding: '1rem', cursor: 'pointer', borderBottom: '1px solid var(--surface-border)' }} onClick={() => handleSort('email')}>Email {sortField === 'email' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                  <th style={{ padding: '1rem', cursor: 'pointer', borderBottom: '1px solid var(--surface-border)' }} onClick={() => handleSort('created_at')}>Joined {sortField === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                  <th style={{ padding: '1rem', cursor: 'pointer', borderBottom: '1px solid var(--surface-border)' }} onClick={() => handleSort('subscription_status')}>Status {sortField === 'subscription_status' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                  <th style={{ padding: '1rem', cursor: 'pointer', borderBottom: '1px solid var(--surface-border)' }} onClick={() => handleSort('has_plaid')}>Bank Linked {sortField === 'has_plaid' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                  <th style={{ padding: '1rem', cursor: 'pointer', borderBottom: '1px solid var(--surface-border)' }} onClick={() => handleSort('api_sync_count')}>API Usage {sortField === 'api_sync_count' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--surface-border)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ fontWeight: 'bold' }}>{u.email}</div>
                      {u.display_name && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{u.display_name}</div>}
                    </td>
                    <td style={{ padding: '1rem', color: 'var(--text-muted)' }}>{new Date(u.created_at).toLocaleDateString()}</td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{ 
                        padding: '0.2rem 0.6rem', 
                        borderRadius: '20px', 
                        fontSize: '0.8rem', 
                        fontWeight: 'bold',
                        background: u.subscription_status === 'active' ? 'rgba(34, 197, 94, 0.1)' : u.subscription_status === 'trialing' ? 'rgba(234, 179, 8, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        color: u.subscription_status === 'active' ? 'var(--success)' : u.subscription_status === 'trialing' ? 'var(--warning)' : 'var(--danger)'
                      }}>
                        {u.subscription_status.toUpperCase()}
                      </span>
                      {u.subscription_status === 'trialing' && (
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
                          Ends: {new Date(u.trial_ends_at).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {u.has_plaid ? <span style={{ color: 'var(--success)' }}>Yes</span> : <span style={{ color: 'var(--text-muted)' }}>No</span>}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {u.api_sync_count || 0} calls
                      <div style={{ fontSize: '0.8rem', color: 'var(--danger)' }}>
                        ~{((u.api_sync_count || 0) * plaidCostPerSync).toLocaleString(undefined, {style: 'currency', currency: 'USD'})}
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <button 
                        className="btn btn-glass" 
                        onClick={() => handleDeleteUser(u.id, u.email)}
                        disabled={deletingId === u.id}
                        style={{ color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.8rem' }}
                      >
                        {deletingId === u.id ? <Loader2 size={16} style={{ animation: 'spin 2s linear infinite' }} /> : <Trash2 size={16} />}
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {sortedUsers.length === 0 && (
                  <tr>
                    <td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
